// The small print of the one funnel, folded on /staff/funnel: page types, what changed, all sources, the joins from email to call or
// card, lead by lead, the punch strip and audiences. A visit means a human session the filter proved. Opened and Clicked are
// gone: open and link tracking are off, so a number there would be invented.
import { Section, Table } from '../kit'
import type { Board, Group, Growth, Join, Journey, Num } from '../../growth/types'
import { dayOf as localDay, current, emailDomain, list, n, plural, requestsSince, short, stalled, sum, today, weekday, when as whenWords, yesterday } from '../../growth/format'
import { funnelColumns, type Funnel } from './funnelData'
import { Bar, Lines, ProspectLink, Ticks, repliesRead, sourceOf } from './parts'

type Stage = 'sent' | 'delivered' | 'still_in' | 'visited' | 'replied' | 'interested' | 'calls' | 'cards'
const STAGES: { key: Stage; label: string }[] = [
  { key: 'sent', label: 'Sent' }, { key: 'delivered', label: 'Did not bounce' }, { key: 'still_in', label: 'Still in a campaign' },
  { key: 'visited', label: 'Proven visit' }, { key: 'replied', label: 'Replied' }, { key: 'interested', label: 'Interested' },
  { key: 'calls', label: 'Call booked' }, { key: 'cards', label: 'Card requested' },
]
const proven = (rows: Journey[] | null, group?: string): Num => (rows === null ? null : rows.filter((r) => r.visit_at && (!group || r.group === group)).length)
const value = (g: Group, s: Stage, rows: Journey[] | null, replies: boolean): Num =>
  s === 'replied' && !replies ? null : s === 'visited' ? proven(rows, g.key) : s === 'delivered' ? (g.sent === null || g.bounced === null ? null : g.sent - g.bounced) : g[s]

export function PageTypes({ d }: { d: Funnel }) {
  return (
    <Section id="pages" title="By page type" testid="funnel-pages">
      <div className="st-fn-flat"><Table flat rows={[...d.pages, d.pages_total]} columns={funnelColumns(d, 'Page')} rowKey={(r) => r.key} testid="funnel-pages-table" /></div>
      <p className="st-note">Home visits aren't split out of the site total. A page comes from the slug on the request link.</p>
    </Section>
  )
}

export function EmailFunnel({ g, drafts }: { g: Growth; drafts: Num | undefined }) {
  const b = g.outreach?.board ?? null
  return (
    <>
      <Changed g={g} b={b} />
      <AllSources g={g} drafts={drafts} />
      {b?.funnel && b.funnel.sent !== null ? <Email b={b} /> : null}
    </>
  )
}

/** Card requests and bookings since yesterday, and whether page views moved. */
function Changed({ g, b }: { g: Growth; b: Board | null }) {
  const requests = requestsSince(g.recent)
  const rows = [
    ...requests.map((r) => ({ at: r.request_submitted_at!, text: `${r.company ?? r.name ?? r.email} requested the card`, domain: emailDomain(r.email) })),
    ...(b?.recent_bookings ?? []).map((k) => ({ at: k.booked_at, domain: k.domain, text: `${k.company} booked a call for ${k.start && localDay(k.start) === localDay(k.booked_at) ? 'the same day' : short(k.start)}` })),
  ].filter((r) => localDay(r.at) >= yesterday()).sort((x, y) => y.at.localeCompare(x.at)).slice(0, 6)
  const [prev, last] = (b?.page_days ?? []).slice(-2)
  // A failed Ploy run has written 0 for a day it never read, so a change is only news when the read worked.
  const views = current(sourceOf(b, 'ploy')) && prev && last && prev.views !== null && last.views !== null && prev.views !== last.views
    ? `Prospect page views ${last.views > prev.views ? 'rose' : 'fell'} to ${n(last.views)} from ${n(prev.views)}` : null
  return (
    <Section title="Since yesterday" testid="growth-answer-changed">
      <p className="st-note">{requests.length ? plural(requests.length, 'card request') : 'No new card requests'}{b?.funnel?.cards === 0 ? ', none from cold email' : ''}</p>
      {rows.map((r, i) => <div key={i} className="st-fn-who"><ProspectLink domain={r.domain}>{r.text}</ProspectLink><span className="st-faint">{localDay(r.at) === today() ? 'Today' : 'Yesterday'}</span></div>)}
      {views ? <p className="st-note">{views}</p> : null}
    </Section>
  )
}

/** Every source, not only email, for one fixed 30 days. Open drafts are counted as of now and say so. */
function AllSources({ g, drafts }: { g: Growth; drafts: Num | undefined }) {
  const chase = stalled(g.recent)
  const booked = sum(g.days.slice(-30).map((x) => x.booked))
  const bookings = sourceOf(g.outreach?.board, 'calendly')
  const names = chase.slice(0, 3).map((r) => r.company ?? r.email)
  return (
    <Section title="All sources, last 30 days" testid="growth-not-email">
      <Lines rows={[
        ['Signups, counted by person', n(g.totals.accounts.last_30)],
        [<>Calls booked{bookings && bookings.state !== 'ok' ? <span className="st-bad">, to {short(bookings.as_of)}</span> : null}</>, n(booked)],
        ['Card requests sent', n(g.totals.submitted.last_30)],
        [<>Card drafts open now, any age{names.length ? `: ${list(names)}` : ''}{chase.length > 3 ? ` and ${chase.length - 3} more` : ''}</>, n(drafts)],
      ]} />
    </Section>
  )
}

function Email({ b }: { b: Board }) {
  const f = b.funnel!
  const emails = sourceOf(b, 'instantly')
  const follow = b.capacity.find((c) => (c.followups ?? 0) > 0)
  const seen = proven(b.journeys)
  const title = f.sent === 0 ? 'No email has gone out yet' : seen === null ? 'Proven visits not read' : `${plural(seen, 'proven visit')} from ${plural(f.sent ?? 0, 'email')}`
  const sub = current(emails)
    ? [`${n(f.calls)} ${f.calls === 1 ? 'call' : 'calls'}, ${n(f.cards)} ${f.cards === 1 ? 'card' : 'cards'}`, f.still_in !== null ? `${n(f.still_in)} still in campaigns` : null, follow ? `follow-ups start ${weekday(follow.date)}` : null].filter(Boolean).join(', ')
    : `Sent numbers stop at ${short(emails?.as_of)}`
  return (
    <div className="st-stack st-fn-email" data-testid="growth-funnel">
      <Section id="email" title="From email to call or card">
        <p><span className="st-strong">{title}</span> <span className="st-muted">{sub}</span></p>
        <Joins rows={b.joins} />
      </Section>
      <Leads rows={b.journeys} />
      <Punch b={b} />
      <Section title="By audience">
        <Table rows={b.groups} rowKey={(r) => r.key} testid="growth-audience" empty="No audiences read" columns={[
          { key: 'label', head: 'Audience', primary: true, cell: (r) => r.label }, { key: 'sent', head: 'Sent', num: true, cell: (r) => n(r.sent) },
          { key: 'still', head: 'Still in', num: true, cell: (r) => n(r.still_in) }, { key: 'replied', head: 'Replied', num: true, cell: (r) => n(repliesRead(b) ? r.replied : null) },
          { key: 'interested', head: 'Interested', num: true, cell: (r) => n(r.interested) }, { key: 'calls', head: 'Calls', num: true, cell: (r) => n(r.calls) },
        ]} />
      </Section>
    </div>
  )
}

function Joins({ rows }: { rows: Join[] }) {
  return (
    <div data-testid="growth-joins">
      {rows.map((r) => (
        <div key={r.key} className="st-fn-join" data-testid={`growth-join-${r.key}`}>
          <span>{r.label}</span>
          <Bar value={r.matched ?? 0} max={r.total ?? 0} />
          <span className="st-num">{n(r.matched)} of {n(r.total)}</span>
          <span className="st-muted">{r.matched === null ? 'Not read' : r.breaks}</span>
        </div>
      ))}
    </div>
  )
}

function Leads({ rows }: { rows: Journey[] | null }) {
  return (
    <Section title="Lead by lead" testid="growth-leads">
      {rows === null ? <p className="st-note">Not read</p> : (
        <Table rows={rows} rowKey={(r) => r.slug ?? r.domain ?? r.company ?? ''} empty="No proven visit, call or card yet" rowAttrs={(r) => ({ 'data-testid': `growth-lead-${r.slug}` })} columns={[
          { key: 'company', head: 'Company', primary: true, cell: (r) => <ProspectLink domain={r.domain}>{r.company ?? r.domain ?? '–'}</ProspectLink> },
          { key: 'sent', head: 'Emailed', cell: (r) => whenWords(r.sent_at) },
          { key: 'visit', head: 'Proven visit', cell: (r) => <>{whenWords(r.visit_at)}{r.visit_why ? <span className="st-muted"> {r.visit_why}</span> : null}</> },
          { key: 'call', head: 'Call or card', cell: (r) => (r.call_at ? `Call ${whenWords(r.call_at)}` : r.card_at ? `Card ${whenWords(r.card_at)}` : '–') },
          { key: 'breaks', head: 'Where it breaks', cell: (r) => <span className="st-muted">{r.breaks ?? '–'}</span> },
        ]} />
      )}
    </Section>
  )
}

function Punch({ b }: { b: Board }) {
  const rows = b.groups.filter((g) => (g.sent ?? 0) > 0)
  const scanners = b.funnel?.scanner_visits
  if (rows.length === 0) return null
  return (
    <Section title="Each email, stage by stage" testid="growth-punch">
      <div className="st-fn-punch st-muted">
        <span /><span />
        <div className="st-fn-strips">{rows.map((g) => <span key={g.key} style={{ flexGrow: g.sent ?? 1 }}>{g.label} {n(g.sent)}</span>)}</div>
      </div>
      {STAGES.map((s) => {
        const v = sum(rows.map((g) => value(g, s.key, b.journeys, repliesRead(b))))
        return (
          <div key={s.key} className="st-fn-punch" data-testid={`growth-stage-${s.key}`}>
            <span>{s.label}</span>
            <span className={v === null ? 'st-num st-muted' : 'st-num'}>{n(v)}</span>
            {v === null ? <span className="st-muted">Not read</span> : (
              <div className="st-fn-strips">{rows.map((g) => <span key={g.key} style={{ flexGrow: g.sent ?? 1 }}><Ticks total={g.sent ?? 0} on={value(g, s.key, b.journeys, repliesRead(b))} name={`${g.label}, ${s.label}`} /></span>)}</div>
            )}
          </div>
        )
      })}
      {scanners ? <p className="st-note">{plural(scanners, 'session')} looked like mail scanners and are not counted as visits</p> : null}
    </Section>
  )
}

